/**
 * Max Quantity Limit Handler
 *
 * Enforces maximum ordering quantity limits on products.
 * Limits are defined via the custom.max_quantity metafield.
 *
 * Features:
 * - Validates quantity on product page before add to cart
 * - Validates quantity changes in cart
 * - Shows bilingual error messages
 * - Considers existing cart quantity when validating
 */

(function() {
  'use strict';

  const MaxQuantity = {
    /**
     * Get translated error messages
     */
    getMessages: function() {
      const isEnglish = window.location.pathname.includes('/en/');
      return {
        exceededLimit: isEnglish
          ? 'Maximum order quantity is {max} per customer. You have {inCart} in your cart.'
          : 'La quantité maximale par commande est de {max} par client. Vous avez {inCart} dans votre panier.',
        limitReached: isEnglish
          ? 'You have reached the maximum order quantity of {max} for this item.'
          : 'Vous avez atteint la quantité maximale de commande de {max} pour cet article.',
        cannotAdd: isEnglish
          ? 'Cannot add more. Maximum quantity is {max}.'
          : 'Impossible d\'ajouter plus. La quantité maximale est de {max}.',
        adjustedQuantity: isEnglish
          ? 'Quantity adjusted to maximum of {max}.'
          : 'Quantité ajustée au maximum de {max}.'
      };
    },

    /**
     * Format message with placeholders
     */
    formatMessage: function(template, values) {
      let message = template;
      for (const [key, value] of Object.entries(values)) {
        message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
      }
      return message;
    },

    /**
     * Get cart quantity for a specific variant
     */
    getCartQuantityForVariant: function(variantId) {
      const input = document.querySelector(`[data-cart-quantity][data-variant-id="${variantId}"]`);
      if (input) {
        return parseInt(input.dataset.cartQuantity) || 0;
      }
      // Fallback: check data attribute on quantity input
      const qtyInput = document.querySelector(`#Quantity-${variantId}, [name="quantity"][data-variant-id="${variantId}"]`);
      if (qtyInput && qtyInput.dataset.cartQuantity) {
        return parseInt(qtyInput.dataset.cartQuantity) || 0;
      }
      return 0;
    },

    /**
     * Validate quantity against max limit
     * Returns { valid: boolean, message: string, adjustedQty: number }
     */
    validate: function(requestedQty, maxQty, currentCartQty = 0) {
      const messages = this.getMessages();
      const totalQty = requestedQty + currentCartQty;

      if (maxQty === null || maxQty === undefined || maxQty <= 0) {
        return { valid: true, message: '', adjustedQty: requestedQty };
      }

      if (currentCartQty >= maxQty) {
        return {
          valid: false,
          message: this.formatMessage(messages.limitReached, { max: maxQty }),
          adjustedQty: 0
        };
      }

      if (totalQty > maxQty) {
        const canAdd = maxQty - currentCartQty;
        return {
          valid: false,
          message: this.formatMessage(messages.exceededLimit, {
            max: maxQty,
            inCart: currentCartQty,
            canAdd: canAdd
          }),
          adjustedQty: canAdd
        };
      }

      return { valid: true, message: '', adjustedQty: requestedQty };
    },

    /**
     * Show error message on product form
     */
    showProductFormError: function(form, message) {
      let errorWrapper = form.querySelector('.product-form__error-message-wrapper');
      if (!errorWrapper) {
        // Create error wrapper if it doesn't exist
        errorWrapper = document.createElement('div');
        errorWrapper.className = 'product-form__error-message-wrapper';
        errorWrapper.innerHTML = '<span class="product-form__error-message"></span>';
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.parentNode.insertBefore(errorWrapper, submitBtn);
        }
      }

      const errorMessage = errorWrapper.querySelector('.product-form__error-message');
      if (errorMessage) {
        errorMessage.textContent = message;
        errorWrapper.removeAttribute('hidden');
        errorWrapper.classList.add('max-qty-error');
      }
    },

    /**
     * Clear error message on product form
     */
    clearProductFormError: function(form) {
      const errorWrapper = form.querySelector('.product-form__error-message-wrapper.max-qty-error');
      if (errorWrapper) {
        errorWrapper.setAttribute('hidden', '');
        errorWrapper.classList.remove('max-qty-error');
      }
    },

    /**
     * Initialize product form validation
     */
    initProductForm: function() {
      const productForms = document.querySelectorAll('product-form form, form[action*="/cart/add"]');

      productForms.forEach(form => {
        // Get max quantity from data attribute
        const productFormEl = form.closest('product-form');
        if (!productFormEl) return;

        const maxQtyEl = productFormEl.querySelector('[data-max-order-quantity]');
        const maxQty = maxQtyEl ? parseInt(maxQtyEl.dataset.maxOrderQuantity) : null;

        if (!maxQty || maxQty <= 0) return;

        // Store max qty on form
        form.dataset.maxOrderQuantity = maxQty;

        // Add submit handler
        form.addEventListener('submit', (e) => {
          const quantityInput = form.querySelector('[name="quantity"]');
          const variantInput = form.querySelector('[name="id"]');

          if (!quantityInput || !variantInput) return;

          const requestedQty = parseInt(quantityInput.value) || 1;
          const variantId = variantInput.value;

          // Get current cart quantity for this product/variant
          const currentCartQty = parseInt(quantityInput.dataset.cartQuantity) || 0;

          const validation = this.validate(requestedQty, maxQty, currentCartQty);

          if (!validation.valid) {
            e.preventDefault();
            e.stopPropagation();
            this.showProductFormError(form, validation.message);

            // Shake animation for feedback
            form.classList.add('max-qty-shake');
            setTimeout(() => form.classList.remove('max-qty-shake'), 500);

            return false;
          } else {
            this.clearProductFormError(form);
          }
        }, true); // Use capture to run before other handlers

        // Also validate on quantity change
        const quantityInput = form.querySelector('[name="quantity"]');
        if (quantityInput) {
          quantityInput.addEventListener('change', () => {
            const requestedQty = parseInt(quantityInput.value) || 1;
            const currentCartQty = parseInt(quantityInput.dataset.cartQuantity) || 0;
            const validation = this.validate(requestedQty, maxQty, currentCartQty);

            if (!validation.valid) {
              this.showProductFormError(form, validation.message);
            } else {
              this.clearProductFormError(form);
            }
          });
        }
      });
    },

    /**
     * Initialize cart quantity validation
     */
    initCart: function() {
      // Track inputs currently being corrected to prevent infinite loops
      const correctingInputs = new WeakSet();

      // Listen for cart quantity changes
      document.addEventListener('change', (e) => {
        const input = e.target.closest('.quantity__input');
        if (!input) return;

        // Prevent infinite loop - skip if we're correcting this input
        if (correctingInputs.has(input)) {
          correctingInputs.delete(input);
          return;
        }

        const maxQty = parseInt(input.dataset.maxOrderQuantity);
        if (!maxQty || maxQty <= 0) return;

        const requestedQty = parseInt(input.value) || 1;

        if (requestedQty > maxQty) {
          const messages = this.getMessages();
          const message = this.formatMessage(messages.cannotAdd, { max: maxQty });

          // Mark this input as being corrected
          correctingInputs.add(input);

          // Set value back to max
          input.value = maxQty;

          // Show error
          const cartItem = input.closest('.cart-item');
          if (cartItem) {
            const errorEl = cartItem.querySelector('.cart-item__error-text');
            if (errorEl) {
              errorEl.textContent = message;
              setTimeout(() => { errorEl.textContent = ''; }, 4000);
            }
          }

          // Prevent the change event from propagating
          e.stopPropagation();

          // Dispatch new change event with corrected value
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, true);
    },

    /**
     * Initialize all max quantity functionality
     */
    init: function() {
      this.initProductForm();
      this.initCart();

      // Re-initialize on dynamic content changes
      document.addEventListener('shopify:section:load', () => {
        this.initProductForm();
      });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => MaxQuantity.init());
  } else {
    MaxQuantity.init();
  }

  // Expose for external use
  window.MaxQuantity = MaxQuantity;
})();
